/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { ACTION_ID } from './modelMapper/appModelMapper/appModelConstants';
import { APP_SECTION_LAYOUTS } from '../layouts/layoutConstants';

const SUBTEXT_TYPE = {
    INFO: 'info',
    ERROR: 'error',
};

const ERROR_DIALOG_FIXES = {

    118: [
        {
            textId: 'ccx.confirmation.retryable.error.fix.118.step1',
        },
    ],
    189: [
        {
            textId: 'ccx.confirmation.retryable.error.fix.189.step1',
        },
    ],
    403: [
        {
            textId: 'ccx.confirmation.retryable.error.fix.403.step1',
        },
    ],
    145: [
        {
            textId: 'ccx.confirmation.retryable.error.fix.145.step1',
        },
    ],
    500: [
        {
            textId: 'ccx.confirmation.retryable.error.fix.500.step1',
        },
    ],
    133: [
        {
            textId: 'ccx.confirmation.retryable.error.fix.133.step1',
            linkId: 'ccx.confirmation.retryable.error.fix.133.steplink',
        },
    ],
};

const APP_DIALOG = {
    DESCRIPTION_TYPE: {
        LIST: 'list',
        PARAGRAPH: 'paragraph',
    },
    INPUT_TYPE: {
        SWITCH: 'switch',
        CHECKBOX: 'checkbox',
        RADIO_WITH_CHECKBOXES: 'radio-with-checkboxes',
    },
};

const PRODUCT_CTA_PRIORITY = {
    [ACTION_ID.INSTALL]: 120,
    [ACTION_ID.TRY]: 110,
    [ACTION_ID.NOT_COMPATIBLE]: 100,
    [ACTION_ID.GET_LINK]: 90,
    [ACTION_ID.LAUNCH_SERVICE]: 80,
    [ACTION_ID.START_TRIAL]: 70,
    [ACTION_ID.OPEN]: 60,
    [ACTION_ID.BUY_NOW]: 50,
    [ACTION_ID.BUY]: 40,
    [ACTION_ID.UPDATE]: 30,
    [ACTION_ID.DISCOVER]: 20,
    [ACTION_ID.LEARN_MORE]: 10,
};

const LABEL_PRIORITY_TYPES = {
    SESSION_ERROR: 60,
    UPDATE_AVAILABLE: 50,
    TRIAL_EXPIRED: 40,
    TRIAL_DAYS_REMAINING_LT_5: 30,
    TRIAL_DAYS_REMAINING_GT_5: 20,
    UP_TO_DATE: 10,
};


const ACTION = {
    COMPONENT: {
        BUTTON: 'button',
        DROPDOWN: 'dropdown',
        MENU: 'menu',
        LABEL: 'label',
    },
};

const ANALYTICS = {
    PDP: 'pdp',
    PDP_forUrlParam: 'PDP',
    APPS: 'apps',
    APP_CARD: 'AppCard',
    RESOURCES: 'Resources',
    BANNER: 'Banner',
    EVENTS: {
        RENDER_PAGE: 'renderPage',
        RENDER_APP: 'appRender',
        HEADER_BAR_BUTTON: 'clickHeaderBarButton',
        HEADER_BAR_MENU_ITEM: 'clickHeaderBarMenuItem',
        BANNER_EVENTS: 'bannerEvents',
        APP_BUTTON_CLICK: 'appButtonClick',
        APP_MENU_CLICK: 'appMenuClick',
        APPS_ARRAY: 'appsArray',
        APP_PROGRESS_CANCEL: 'progressCancel',
        APP_TILE_CLICK: 'appTileClick',
        MORE_INFO_CLICK: 'moreInfoClick',
        SEE_MORE_CLICK: 'seeMoreClick',
        UPDATE_AVAILABLE: 'updateAvailable',
        NO_CONNECTION_BTN_CLICK: 'noConnectionButtonClick',
        RENDER_CONNECTION_FAILED_PAGE: 'noConnectionRenderPage',
    },
    LEFT_NAV: {
        all: 'All Apps',
        updates: 'Updates',
        photography: 'Photography',
        design: 'Design & Layout',
        'video-audio': 'Video & Motion',
        illustration: 'Illustration',
        'ux-web': 'UI & UX',
        'social-media': 'Social Media',
        ar: '3D & AR',
        'acrobat-pdf': 'Acrobat & PDF',
    },
    LEFT_NAV_SUBTYPE: {
        all: 'all-apps',
    },
    PLATFORM: {
        desktop: 'Desktop',
        mobile: 'Mobile',
        web: 'Web',
    },
    ENTITLED_STATUS: {
        subscribed: 'entitled',
    },
    INSTALLATION_STATUS: {
        ENTITLED_INSTALLED: 'Entitled Installed',
        ENTITLED_AVAILABLE: 'Entitled Available',
        TRIAL_INSTALLED: 'Trial Installed',
        TRIAL_AVAILABLE: 'Trial Available',
    },
    INSTALLATION_STATE: {
        NOT_INSTALLED: 'not-installed',
        INSTALLED: 'installed',
    },
    ENTITLEMENT: {
        NOT_ENTITLED: 'not-entitled',
        ENTITLED: 'entitled',
    },
    COMPATIBILITY: {
        NOT_COMPATIBLE: 'not-compatible',
        COMPATIBLE: 'compatible',
    },
    SURFACE_TYPE: {
        SKELETON: 'skeleton',
        CATALOG: 'catalog',
        CATEGORY_BANNER: 'categoryBanner',
        ALL_APPS_BANNER: 'appsBanner',
    },
    UPDATE_SECTIONS: {
        NEW_UPDATES: 'New Updates',
        UPDATED_RECENTLY: 'Updated Recently',
    },
    VIEW_TYPE: {
        [APP_SECTION_LAYOUTS.DEFAULT]: 'card',
        [APP_SECTION_LAYOUTS.CARD_VIEW]: 'card',
        [APP_SECTION_LAYOUTS.LIST_VIEW]: 'list',
        [APP_SECTION_LAYOUTS.CARD_MEDIA_TAGLINE_VIEW]: 'appWithMedia',
        [APP_SECTION_LAYOUTS.CARD_MEDIA_TAGLINE_FEATURE_VIEW]: 'appWithMediaAndFeatures',
        [APP_SECTION_LAYOUTS.CARD_MEDIA_DESC_LEFT_VIEW]: 'appWithMediaOnLeft',
        [APP_SECTION_LAYOUTS.CARD_MEDIA_DESC_RIGHT_VIEW]: 'appWithMediaOnRight',
    },
};

const LEFT_NAV_KEYS = {
    APPS_SECTION_GROUP_KEY: 'apps',
    CATEGORIES_SECTION_GROUP_KEY: 'categories',
    ALL_APPS_KEY: 'all',
    UPDATES_KEY: 'updates',
    FONTS_SECTION_GROUP_KEY: 'fonts',
    FONTS_PANEL_SECTION_GROUP_KEY: 'fonts-panel',
};

const SKELETON_CTA_KEYS = {
    UPDATE_ALL_KEY: 'update-all',
};

const SKELETON_MENU_KEYS = {
    ENABLE_AU_KEY: 'menu-auto-update',
    CFU_KEY: 'menu-cfu',
};

const APPINFO_ACTION_ID = {
    DONE: 'done-click',
    VIEW_MORE: 'view-more-click',
    INSTALL: 'appinfo-install',
    UNINSTALL: 'appinfo-uninstall',
    NOT_COMPATIBLE: 'appinfo-notcompatible',
    LTS: 'long-term-supported-click',
};

const INCOMPATIBLE_ACTION_ID = {
    LEARN_MORE: 'incompatible-learn-more',
    OK: 'incompatible-ok',
};

const PDP_ACTION = {
    BACK_CLICK: 'back-click',
    VIEW_MORE_CLICK: 'view-more-click',
    HEADING_CLICK: 'heading-click',
    APP_CLICK: 'app-click',
    CATEGORY_LABEL_CLICK: 'category-label-click',
    CARD_CLICK: 'card-click',
    TUTORIAL_CLICK: 'tutorial-click',
    VIDEO_BANNER_CLICK: 'video-banner-click',
    TROUBLESHOOTING_AND_HELP: 'troubleshooting-and-help',
    CONTACT_US: 'contact-us',
    TUTORIALS: 'tutorials',
    COMMUNITY_AND_FORUM: 'community-and-forum',
    FACEBOOK: 'facebook',
    TWITTER: 'twitter',
    YOUTUBE: 'youtube',
    INSTAGRAM: 'instagram',
};

const CATEGORY_IDS = {
    BETA_APPS: 'beta-apps',
    PRE_RELEASE: 'prerelease',
};

const INFORMATION_DIALOG_ID = {
    DONE_CLICK: 'done-click',
};

const LOG_LEVEL = {
    NONE: 0,
    ERROR: 1,
    WARN: 2,
    INFO: 3,
    DEBUG: 4,
};

const APPS_COMPONENT_ID = 'CCAppsUI';

const DISCOVER_ALL_SAPCODE_KEY = 'ALL';

const UPDATE_ALL = 'update_all';

export {
    PDP_ACTION,
    SUBTEXT_TYPE,
    APP_DIALOG,
    ACTION,
    ANALYTICS,
    LEFT_NAV_KEYS,
    SKELETON_CTA_KEYS,
    SKELETON_MENU_KEYS,
    APPINFO_ACTION_ID,
    INCOMPATIBLE_ACTION_ID,
    INFORMATION_DIALOG_ID,
    APPS_COMPONENT_ID,
    DISCOVER_ALL_SAPCODE_KEY,
    ERROR_DIALOG_FIXES,
    CATEGORY_IDS,
    PRODUCT_CTA_PRIORITY,
    LOG_LEVEL,
    LABEL_PRIORITY_TYPES,
    UPDATE_ALL,
};
